import { Box, Button, Group, Stack, Text, ThemeIcon } from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { isEqual } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { FileList } from "./components";
import { formatFileSize, isAttachment } from "./utils";

import { TextSerif } from "@/components/elements";
import { IconUpload } from "@/components/icons";
import { showCustomNotification } from "@/utils/notifications";

import type { Attachment } from "@/types/attachment";
import type { MantineStyleProps } from "@mantine/core";
import type { DropzoneProps, FileRejection, FileWithPath } from "@mantine/dropzone";
import type { FC } from "react";
import type { FileListProps } from "./components";
import type { FileAttachment, FileUploaded } from "./types";

export type UploadData = {
  file: FileWithPath;
  id: string;
};

export type UploadCallbackFunc = (id: string, progress: number, file?: Attachment) => void;

/**
 * Labels for customizing the text displayed in the FileUploader component.
 *
 * You can use the special tag `{{maxSize}}` to display the maximum uploadable file size in the string.
 *
 * @property buttonCancel - Label for the cancel button.
 * @property buttonSelect - Label for the select files button.
 * @property buttonSubmit - Label for the submit/upload button.
 * @property dropzoneDescription - Description text for the dropzone area. Supports `{{maxSize}}` tags.
 * @property dropzoneTitle - Title text for the dropzone area.
 * @property listTitle - Title for the list of selected files.
 */
export type FileUploaderLabels = {
  buttonCancel?: string;
  buttonSelect?: string;
  buttonSubmit?: string;
  dropzoneDescription?: string;
  dropzoneTitle?: string;
  listTitle?: string;
} & FileListProps["labels"];

export type FileUploaderProps = {
  buttonsAlign?: "center" | "left" | "right";
  files?: FileAttachment[];
  labels?: FileUploaderLabels;
  onCancel?: () => void;
  onCancelUpload?: (id: string) => void;
  onDropError?: (files: FileRejection[]) => void;
  onDropUpload?: (data: UploadData, callback: UploadCallbackFunc) => void;
  onSave: (files: FileAttachment[]) => void;
  hideSaveButton?: boolean;
  types?: FileListProps["types"];
} & Pick<DropzoneProps, "accept" | "disabled" | "loading" | "maxFiles" | "maxSize" | "multiple"> &
  MantineStyleProps;

// TODO:
// - handling image resize

export const FileUploader: FC<FileUploaderProps> = ({
  buttonsAlign = "center",
  accept,
  disabled = false,
  files = [],
  labels: customLabels,
  loading = false,
  maxFiles,
  maxSize,
  multiple = true,
  onCancel,
  onCancelUpload,
  onDropError,
  onDropUpload,
  onSave,
  hideSaveButton = false,
  types,
  ...props
}) => {
  const { t } = useTranslation();
  const [currentFiles, setFiles] = useState(files);
  const defaultLabels: FileUploaderLabels = {
    buttonCancel: t("components.FileUploader.buttonCancel"),
    buttonSelect: t("components.FileUploader.buttonSelect"),
    buttonSubmit: t("components.FileUploader.buttonSubmit"),
    dropzoneTitle: t("components.FileUploader.dropzoneTitle"),
    listTitle: t("components.FileUploader.listTitle"),
  };

  const isUploading =
    !!onDropUpload &&
    currentFiles.some((value) => !isAttachment(value) && (value.progress ?? 0) < 100);
  const canSave = !isEqual(files, currentFiles) && !isUploading;
  const labels = { ...defaultLabels, ...customLabels };

  function attachCallbackToFile(file: FileWithPath, index?: number) {
    if (onDropUpload) {
      const id = [index, new Date().getTime()].filter(Boolean).join("_");
      const uploadingFile: FileUploaded = {
        file,
        id,
        progress: 0,
      };
      onDropUpload({ file, id }, uploadCallback);

      return uploadingFile;
    }
    return { file };
  }

  function handleChange(index: number, file: Partial<FileAttachment>) {
    setFiles((values) => {
      return values.map((value, currIndex) => {
        if (currIndex === index) {
          return { ...value, ...file } as FileAttachment;
        }
        return value;
      });
    });
  }

  function handleDrop(newFiles: FileWithPath[]) {
    setFiles((values) => {
      const existing = values.map((v) => (isAttachment(v) ? v.name : v.file.name));
      const existingSizes = values.map((v) => (isAttachment(v) ? v.size : v.file.size));

      const filtered = newFiles.filter(
        (file) => !existing.some((name, i) => name === file.name && existingSizes[i] === file.size)
      );

      if (filtered.length < newFiles.length) {
        showCustomNotification({
          title: t("components.FileUploader.error.duplicateTitle"),
          message: t("components.FileUploader.error.duplicate"),
          autoClose: 6000,
        });
      }

      return [...filtered.map(attachCallbackToFile), ...values];
    });
  }

  function handleDelete(index: number) {
    if (
      onCancelUpload &&
      !isAttachment(currentFiles[index]) &&
      currentFiles[index].id &&
      currentFiles[index].progress
    ) {
      onCancelUpload(currentFiles[index].id);
    }
    setFiles((values) => values.toSpliced(index, 1));
  }

  function uploadCallback(id: string, progress: number, _file?: Attachment) {
    setFiles((values) => {
      if (progress === 100) {
        // Remove temporary file after upload completes, the server will provide the real attachment
        return values.filter((v) => v.id !== id);
      }
      return values.map((value) => {
        if (value.id === id) {
          return { ...value, progress };
        }
        return value;
      });
    });
  }

  function handleRejections(rejections: FileRejection[]) {
    rejections.map((r) =>
      r.errors.map((e) =>
        showCustomNotification({
          title: t("components.FileUploader.error.title", { fileName: r.file.name }),
          message: e.message,
          autoClose: 6000,
        })
      )
    );
  }

  return (
    <Box {...props}>
      <Stack>
        <Dropzone
          accept={accept}
          disabled={disabled || loading}
          maxFiles={maxFiles}
          maxSize={maxSize}
          multiple={multiple}
          onDrop={handleDrop}
          onReject={onDropError ? onDropError : (rejections) => handleRejections(rejections)}
        >
          <Stack gap="xl" py="xl" ta="center">
            <ThemeIcon mx="auto" radius="xl" size="xl" variant="light">
              <IconUpload size={28} />
            </ThemeIcon>
            <Stack gap="xs">
              <Text fw="bold" size="lg">
                {labels.dropzoneTitle}
              </Text>
              {labels.dropzoneDescription && (
                <TextSerif size="sm">
                  {labels.dropzoneDescription.replace("{{maxSize}}", formatFileSize(maxSize || 0))}
                </TextSerif>
              )}
            </Stack>
            <Button
              mx="auto"
              maw={220}
              fullWidth
              disabled={disabled || loading}
              aria-label={labels.buttonSelect || ""}
            >
              {labels.buttonSelect}
            </Button>
          </Stack>
        </Dropzone>
        {!hideSaveButton && (
          <FileList
            disabled={disabled || loading}
            files={currentFiles}
            labels={labels}
            onChange={handleChange}
            onDelete={handleDelete}
            title={labels.listTitle}
            types={types}
          />
        )}
        <Group justify="center">
          {onCancel && (
            <Button
              variant="outline"
              maw={220}
              fullWidth
              onClick={onCancel}
              disabled={loading}
              aria-label={labels.buttonCancel || ""}
            >
              {labels.buttonCancel}
            </Button>
          )}
          {!hideSaveButton && (
            <Button
              maw={220}
              fullWidth
              disabled={!canSave || isUploading}
              onClick={() => onSave(currentFiles)}
              loading={loading || isUploading}
              aria-label={labels.buttonSubmit || ""}
            >
              {labels.buttonSubmit}
            </Button>
          )}
        </Group>
      </Stack>
    </Box>
  );
};
